import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
// import { useNotification } from '../../hooks/useNotification';
import apiService from '../../services/shared/apiService';
import { Patient } from '../../shared/types/src';
import PatientForm, { PatientFormData } from './PatientForm';
import { useTheme } from '../../context/shared/ThemeContext'

interface ModifyPatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPatientUpdated: () => void;
  patient: Patient | null;
}

const ModifyPatientModal: React.FC<ModifyPatientModalProps> = ({ isOpen, onClose, onPatientUpdated, patient }) => {
  const { themeClasses } = useTheme();
  // const { addToast } = useNotification();
  const [formData, setFormData] = useState<PatientFormData | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (patient) {
      // Safely map patient data to form data, providing defaults for missing fields
      setFormData({
        email: patient.email || '',
        displayName: patient.name || '',
        dob: (patient as any).dob || '',
        sex: (patient as any).sex || '',
        pronouns: (patient as any).pronouns || '',
        // Provide default empty strings for fields that may not exist on the Patient type
        phoneNumber: (patient as any).phoneNumber || '',
        street1: (patient as any).street1 || '',
        city: (patient as any).city || '',
        state: (patient as any).state || '',
        zip: (patient as any).zip || '',
        country: (patient as any).country || '',
        emergencyContactName: (patient as any).emergencyContactName || '',
        emergencyContactPhone: (patient as any).emergencyContactPhone || '',
        primaryConcerns: (patient as any).primaryConcerns || '',
        pastDiagnoses: Array.isArray((patient as any).pastDiagnoses) 
          ? (patient as any).pastDiagnoses.join(', ') 
          : (patient as any).pastDiagnoses || '',
        medicationList: Array.isArray((patient as any).medications) 
          ? (patient as any).medications.join(', ') 
          : (patient as any).medicationList || '',
        treatmentPlanSummary: (patient as any).treatmentPlanSummary || '',
        // Medical fields
        allergies: Array.isArray((patient as any).allergies) 
          ? (patient as any).allergies.join(', ') 
          : (patient as any).allergies || '',
        conditions: Array.isArray((patient as any).conditions) 
          ? (patient as any).conditions.join(', ') 
          : (patient as any).conditions || '',
        insurance: (patient as any).insurance || '',
        primaryCarePhysician: (patient as any).primaryCarePhysician || '',
        lastPhysical: (patient as any).lastPhysical || '',
        // Clinical fields
        goals: Array.isArray((patient as any).goals) 
          ? (patient as any).goals.join(', ') 
          : (patient as any).goals || '',
        riskFactors: Array.isArray((patient as any).riskFactors) 
          ? (patient as any).riskFactors.join(', ') 
          : (patient as any).riskFactors || '',
        lastSession: (patient as any).lastSession || '',
        nextSession: (patient as any).nextSession || '',
        therapist: (patient as any).therapist || '',
        // Consents
        consentMetrics: (patient as any).consentMetrics ?? true,
        consentMessaging: (patient as any).consentMessaging ?? true,
      });
    }
  }, [patient]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => (prev ? { ...prev, [name]: type === 'checkbox' ? checked : value } : null));
  };

  const handleUpdate = async () => {
    if (!patient || !formData) return;
    setIsSaving(true);
    try {
      // Convert string fields back to arrays for API
      const updateData = {
        ...formData,
        medications: formData.medicationList 
          ? formData.medicationList.split(',').map(med => med.trim()).filter(med => med.length > 0)
          : [],
        pastDiagnoses: formData.pastDiagnoses 
          ? formData.pastDiagnoses.split(',').map(diag => diag.trim()).filter(diag => diag.length > 0)
          : [],
        // Convert new array fields
        allergies: formData.allergies 
          ? formData.allergies.split(',').map(allergy => allergy.trim()).filter(allergy => allergy.length > 0)
          : [],
        conditions: formData.conditions 
          ? formData.conditions.split(',').map(condition => condition.trim()).filter(condition => condition.length > 0)
          : [],
        goals: formData.goals 
          ? formData.goals.split(',').map(goal => goal.trim()).filter(goal => goal.length > 0)
          : [],
        riskFactors: formData.riskFactors 
          ? formData.riskFactors.split(',').map(risk => risk.trim()).filter(risk => risk.length > 0)
          : []
      };
      
      await apiService.makeRequest(`/api/users/${patient.uid}`, {
        method: 'PATCH',
        data: updateData,
      });
      // addToast('Patient updated successfully!', 'success');
      onPatientUpdated();
      onClose();
    } catch (error: any) {
      console.error('Failed to update patient:', error);
      // addToast(error.message || 'Failed to update patient.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <div className={`p-4 sm:p-6 max-h-[90vh] overflow-y-auto ${themeClasses.cardBg}`}>
        <h2 className={`text-xl font-bold mb-6 text-center ${themeClasses.primaryText}`}>Modify Patient Details</h2>
        
        {formData ? (
          <PatientForm formData={formData} onFormChange={handleChange} />
        ) : (
          <p>Loading patient data...</p>
        )}

        <div className="mt-8 flex justify-end space-x-3">
          <button type="button" className={`px-4 py-2 rounded-md ${themeClasses.buttonSecondary}`} onClick={onClose}>
            Cancel
          </button>
          <button 
            type="button" 
            className={`px-4 py-2 rounded-md ${themeClasses.buttonPrimary}`} 
            onClick={handleUpdate}
            disabled={isSaving || !formData}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ModifyPatientModal; 